import React, { useEffect, useMemo, useState } from "react";
import { CalendarDays } from "lucide-react";
import DashboardCard from "../components/DashboardCard";
import DashboardEmptyState from "../components/DashboardEmptyState";
import DashboardPeriodFilter from "../components/DashboardPeriodFilter";
import {
  DashboardPeriodPreset,
  IOwnerVisitsData,
  IVisitsSection,
  OwnerVisitsRequestStatus,
  VisitsPropertyFilter,
} from "../types/ownerDashboard.types";
import { resolvePastVisitsTotal, hasVisitsContent } from "../utils/resolvePastVisitsTotal";
import { getDashboardPeriodLabel } from "../utils/getDashboardPeriodLabel";
import { getVisitsPropertyOptions } from "../utils/filterVisitsByProperty";
import VisitsScheduleSkeletonLoader from "./Skeleton";
import PropertyFilter from "./PropertyFilter";
import VisitItemsList from "./VisitItemsList";
import { RentedPropertyCatalog } from "../utils/buildRentedPropertyCatalog";

interface VisitsScheduleProps {
  visitsData: IOwnerVisitsData | null;
  status: OwnerVisitsRequestStatus;
  visitsPeriodPreset: DashboardPeriodPreset;
  selectedPropertyId: number | null;
  schedulesFallback?: number;
  smDown: boolean;
  lgDown?: boolean;
  rentedPropertyCatalog?: RentedPropertyCatalog;
  onPeriodChange: (period: DashboardPeriodPreset) => void;
  onPropertyChange: (propertyId: number | null) => void;
  onPastPageChange: (page: number) => void;
  onUpcomingPageChange: (page: number) => void;
}

type VisitsTab = "past" | "upcoming";

function getCardPadding(smDown: boolean, lgDown: boolean) {
  if (smDown) return "1rem";
  if (lgDown) return "1.25rem";
  return "1.5rem";
}

function renderSectionContent(
  section: IVisitsSection,
  variant: VisitsTab,
  emptyMessage: string,
  pagination: IVisitsSection["pagination"],
  onPageChange: (page: number) => void,
  rentedPropertyCatalog?: RentedPropertyCatalog
) {
  if (section.items.length > 0) {
    return (
      <VisitItemsList
        visits={section.items}
        variant={variant}
        pagination={pagination}
        onPageChange={onPageChange}
        rentedPropertyCatalog={rentedPropertyCatalog}
      />
    );
  }

  if (section.total > 0) {
    return (
      <DashboardEmptyState
        align="center"
        title="Visitas fora do período exibido"
        description={emptyMessage}
        className="py-2"
      />
    );
  }

  return (
    <DashboardEmptyState
      align="center"
      title="Nenhuma visita nesta categoria"
      description="Não há visitas registradas para o filtro selecionado."
      className="py-2"
    />
  );
}

export default function VisitsSchedule({
  visitsData,
  status,
  visitsPeriodPreset,
  selectedPropertyId,
  schedulesFallback,
  smDown,
  lgDown = false,
  onPeriodChange,
  onPropertyChange,
  onPastPageChange,
  onUpcomingPageChange,
  rentedPropertyCatalog,
}: VisitsScheduleProps) {
  const [activeTab, setActiveTab] = useState<VisitsTab>("past");

  const periodLabel = getDashboardPeriodLabel(
    visitsPeriodPreset,
    visitsData?.period ?? null
  );

  const propertyOptions = useMemo(
    () => (visitsData ? getVisitsPropertyOptions(visitsData) : []),
    [visitsData]
  );

  const selectedProperty: VisitsPropertyFilter = selectedPropertyId ?? "all";

  useEffect(() => {
    setActiveTab("past");
  }, [visitsPeriodPreset, selectedPropertyId, visitsData?.period.initial_date]);

  const pastVisitsTotal = resolvePastVisitsTotal(visitsData, schedulesFallback);
  const hasContent = hasVisitsContent(visitsData);
  const showGlobalEmpty =
    status === "success" && !hasContent && pastVisitsTotal === 0;

  const upcomingTotal = visitsData?.upcoming_visits.total ?? 0;
  const pastTotal = visitsData?.past_visits.total ?? pastVisitsTotal;

  let body: React.ReactNode = null;

  if (status === "loading" || status === "idle") {
    body = <VisitsScheduleSkeletonLoader />;
  } else if (status === "error") {
    body = (
      <div className="flex flex-col items-center gap-2 py-8 text-center">
        <p className="text-sm text-[#8b949e]">Não foi possível carregar a agenda de visitas.</p>
        {schedulesFallback != null && schedulesFallback > 0 && (
          <p className="text-xs text-[#6e7681]">
            Total registrado no dashboard: {schedulesFallback} visita
            {schedulesFallback !== 1 ? "s" : ""}.
          </p>
        )}
      </div>
    );
  } else {
    // Keep period/property filters visible on empty results so the owner can widen the range.
    body = (
      <div className="flex w-full flex-col gap-4">
        <div className="grid w-full grid-cols-1 gap-3 sm:grid-cols-2">
          <DashboardPeriodFilter
            value={visitsPeriodPreset}
            onChange={onPeriodChange}
            label="Período"
            compact
          />
          <PropertyFilter
            properties={propertyOptions}
            selectedProperty={selectedProperty}
            rentedPropertyCatalog={rentedPropertyCatalog}
            onSelectProperty={(propertyId) =>
              onPropertyChange(propertyId === "all" ? null : propertyId)
            }
          />
        </div>

        {showGlobalEmpty ? (
          <DashboardEmptyState
            align="center"
            icon={CalendarDays}
            title="Nenhuma visita agendada"
            description="Quando houver visitas marcadas aos seus imóveis, elas aparecerão aqui. Você também pode ampliar o período acima."
            className="py-6"
          />
        ) : !hasContent ? (
          <DashboardEmptyState
            align="center"
            title="Nenhuma visita para este filtro"
            description="Selecione outro período ou imóvel, ou aguarde novas visitas agendadas."
            className="py-2"
          />
        ) : visitsData ? (
          <>
            <div className="flex w-full gap-2 rounded-2xl border border-white/[0.08] bg-[#0d1117]/60 p-1">
              <button
                type="button"
                onClick={() => setActiveTab("past")}
                className={`flex-1 rounded-xl px-3 py-2 text-xs font-semibold transition-colors sm:text-sm ${
                  activeTab === "past"
                    ? "bg-[#6ea8ff]/20 text-[#6ea8ff]"
                    : "text-[#8b949e] hover:bg-white/5"
                }`}
              >
                Realizadas ({pastTotal})
              </button>
              <button
                type="button"
                onClick={() => setActiveTab("upcoming")}
                className={`flex-1 rounded-xl px-3 py-2 text-xs font-semibold transition-colors sm:text-sm ${
                  activeTab === "upcoming"
                    ? "bg-[#6ea8ff]/20 text-[#6ea8ff]"
                    : "text-[#8b949e] hover:bg-white/5"
                }`}
              >
                Próximas ({upcomingTotal})
              </button>
            </div>

            {activeTab === "past"
              ? renderSectionContent(
                  visitsData.past_visits,
                  "past",
                  "Visitas realizadas no período, sem detalhamento individual.",
                  visitsData.past_visits.pagination,
                  onPastPageChange,
                  rentedPropertyCatalog
                )
              : renderSectionContent(
                  visitsData.upcoming_visits,
                  "upcoming",
                  "Próximas visitas agendadas, sem detalhamento individual.",
                  visitsData.upcoming_visits.pagination,
                  onUpcomingPageChange,
                  rentedPropertyCatalog
                )}
          </>
        ) : null}
      </div>
    );
  }

  return (
    <DashboardCard padding={getCardPadding(smDown, lgDown)}>
      <div className="flex w-full flex-col gap-4">
        <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <CalendarDays className="h-5 w-5 shrink-0 text-[#6ea8ff]" />
              <h2 className="text-base font-bold text-[#f0f6fc] sm:text-lg">Agenda de Visitas</h2>
            </div>
            <p className="mt-1 text-xs text-[#8b949e] sm:text-sm">
              Visitas realizadas nos {periodLabel}
            </p>
          </div>
          {status === "success" && pastVisitsTotal > 0 && (
            <div className="flex shrink-0 items-center gap-2 self-start rounded-2xl border border-[#6ea8ff]/25 bg-[#6ea8ff]/10 px-4 py-2">
              <span className="text-xl font-bold text-[#6ea8ff]">{pastVisitsTotal}</span>
              <span className="text-[0.65rem] font-semibold uppercase tracking-wider text-[#8b949e]">
                {periodLabel}
              </span>
            </div>
          )}
        </div>
        {body}
      </div>
    </DashboardCard>
  );
}
